import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api, { DB_TYPE } from '../../api';
import { db } from '../../firebase';
import { getDoc, doc, getDocs, collection, query, where, addDoc, orderBy, limit } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { useT } from '../../utils/useTranslation';
import heroBg from '../../assets/sas_hero_new.png';
import ajrakTexture from '../../assets/ajrak_texture_new.jpg';
import { motion } from 'framer-motion';
import HeroBackground from '../../components/ui/HeroBackground';
import toast from 'react-hot-toast';

export default function BranchDetail() {
    const t = useT();
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [branch, setBranch] = useState(null);
    const [members, setMembers] = useState([]);
    const [activity, setActivity] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hasJoined, setHasJoined] = useState(false);
    const isRTL = t.language === 'sindhi';

    useEffect(() => {
        loadBranch();
    }, [id]);

    const loadBranch = async () => {
        try {
            setLoading(true);
            let branchData = null;
            
            if (DB_TYPE === 'firebase') {
                const branchSnap = await getDoc(doc(db, 'branches', id));
                if (branchSnap.exists()) {
                    branchData = { _id: branchSnap.id, ...branchSnap.data() };
                    
                    // Fetch members and activity logs in parallel for speed
                    const [mSnap, aSnap] = await Promise.all([
                        getDocs(query(collection(db, 'members'), where('current_branch_id', '==', id), where('status', '==', 'approved'))),
                        getDocs(query(collection(db, 'activity_logs'), where('branchId', '==', id), orderBy('timestamp', 'desc'), limit(20))).catch(err => {
                            console.warn("Activity logs query failed (probably missing index):", err);
                            return { docs: [] }; // Fallback to empty logs if query fails
                        })
                    ]);
                    
                    setMembers(mSnap.docs.map(d => ({ _id: d.id, ...d.data() })));
                    setActivity(aSnap.docs.map(d => ({ _id: d.id, ...d.data() })));
                }
            } else {
                const res = await api.get(`/branches/${id}`);
                branchData = res.data;
                setMembers(res.data.members || []);
                setActivity(res.data.activity || []);
            }

            if (branchData) {
                const sId = branchData.secretaryId || branchData.secretary_id || branchData.secretaryID;
                if (sId && (!branchData.secretary || (!branchData.secretary.full_name && !branchData.secretary.name))) {
                    try {
                        const sSnap = await getDoc(doc(db, 'members', sId));
                        if (sSnap.exists()) {
                            branchData.secretary = { _id: sSnap.id, ...sSnap.data() };
                        } else {
                            const uSnap = await getDoc(doc(db, 'users', sId));
                            if (uSnap.exists()) branchData.secretary = { _id: uSnap.id, ...uSnap.data() };
                        }
                    } catch (err) { console.error("Secretary fetch error:", err); }
                }
                setBranch(branchData);
            } else {
                toast.error('Branch not found');
                navigate('/branches');
            }
        } catch (e) {
            console.error(e);
            toast.error('Error loading branch details');
        } finally {
            setLoading(false);
        }
    };

    const handleJoinRequest = async () => {
        if (!user) {
            navigate('/login');
            return;
        }
        try {
            if (DB_TYPE === 'firebase') {
                await addDoc(collection(db, 'branch_requests'), {
                    branchId: id,
                    userId: user.uid,
                    status: 'pending',
                    requestDate: new Date().toISOString(),
                    userName: user.displayName || user.name || 'Unknown Member'
                });
            } else {
                await api.post(`/branches/${id}/join`);
            }
            toast.success('Join request submitted! Waiting for approval.');
            setHasJoined(true);
        } catch (e) {
            console.error(e);
            toast.error('Failed to submit request');
        }
    };

    if (loading || !branch) return (
        <div>
            <div className="hero-premium">
                <HeroBackground />
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', position: 'relative', zIndex: 10 }}>
                    <div className="spinner" />
                </div>
            </div>
        </div>
    );

    return (
        <div>
            {/* Hero Section */}
            <div className="hero-premium" style={{
                padding: '120px 2rem 140px'
            }}>
                <HeroBackground />

                <div style={{ maxWidth: 1200, margin: '0 auto', position: 'relative', zIndex: 10 }}>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <Link to="/branches" style={{
                            color: 'var(--sindhi-gold)', textDecoration: 'none', fontSize: 13, fontWeight: 800,
                            display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 32,
                            background: 'rgba(255,255,255,0.05)', padding: '8px 16px', borderRadius: 100,
                            border: '1px solid rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)',
                            textTransform: 'uppercase', letterSpacing: 1
                        }}>
                            {t('allBranchesBtn')}
                        </Link>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 40 }}>
                            <div style={{ flex: 1, minWidth: 300 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                                    <span style={{ padding: '6px 12px', background: 'var(--sindhi-gold)', color: 'var(--sindhi-navy)', fontSize: 11, fontWeight: 900, borderRadius: 6, textTransform: 'uppercase', letterSpacing: 1 }}>{t('activeBranch')}</span>
                                    <span style={{ width: 40, height: 1, background: 'rgba(255,255,255,0.2)' }}></span>
                                    <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', fontWeight: 600 }}>{t('branchId')}: {id.slice(-8).toUpperCase()}</span>
                                </div>
                                <h1 className="text-gradient-gold" style={{ fontSize: 56, fontWeight: 800, marginBottom: 12, letterSpacing: -1, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit', textAlign: isRTL ? 'right' : 'left' }}>
                                    {isRTL ? (branch.nameSindhi || branch.name) : branch.name}
                                </h1>
                                {!isRTL && branch.nameSindhi && (
                                    <p style={{ fontSize: 32, fontFamily: 'var(--font-sindhi)', color: 'var(--sindhi-gold)', marginBottom: 24, textShadow: '0 4px 12px rgba(0,0,0,0.3)', textAlign: 'left' }}>
                                        {branch.nameSindhi}
                                    </p>
                                )}
                                {isRTL && branch.name && (
                                    <p style={{ fontSize: 24, color: 'rgba(255,255,255,0.6)', marginBottom: 24, textAlign: 'right' }}>
                                        {branch.name}
                                    </p>
                                )}
                                <div style={{ display: 'flex', gap: 32, fontSize: 16, color: 'rgba(255,255,255,0.7)', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                                        <span style={{ color: 'var(--sindhi-gold)' }}>📍</span>
                                        {isRTL ? (branch.cityVillageSindhi || branch.cityVillage) : (branch.cityVillage || branch.city)}
                                    </div>
                                    {branch.phone && <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}><span style={{ color: 'var(--sindhi-gold)' }}>📞</span> {branch.phone}</div>}
                                </div>
                            </div>

                            {user && (
                                <motion.button
                                    whileHover={{ scale: 1.05, y: -2 }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={handleJoinRequest}
                                    disabled={hasJoined}
                                    className="btn-premium"
                                    style={{ height: 64, padding: '0 40px', fontSize: 16, fontWeight: 800 }}
                                >
                                    {hasJoined ? t('requestSubmitted') : t('joinBranchAdd')}
                                </motion.button>
                            )}
                        </div>
                    </motion.div>
                </div>
            </div>

            <div style={{
                background: 'var(--sindhi-ivory)',
                backgroundImage: `url(${ajrakTexture})`,
                backgroundSize: '600px 600px',
                backgroundRepeat: 'repeat',
                position: 'relative',
                zIndex: 10,
                marginTop: '-70px'
            }}>
                <div style={{
                    position: 'absolute',
                    inset: 0,
                    backdropFilter: 'blur(3px)',
                    WebkitBackdropFilter: 'blur(3px)',
                    background: 'rgba(253,248,235,0.7)',
                    zIndex: 0,
                    pointerEvents: 'none'
                }} />
                <div style={{
                    maxWidth: 1200,
                    margin: '0 auto',
                    padding: '0 24px 100px',
                    position: 'relative',
                    zIndex: 1
                }}>
                    {/* About & Stats Grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1.8fr 1fr', gap: 40, marginBottom: 56 }}>
                        {/* About */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                            className="card-royal"
                            style={{ padding: 48, background: 'white' }}
                        >
                            <div style={{ marginBottom: 40, textAlign: isRTL ? 'right' : 'left' }}>
                                <h2 className="text-gradient-gold" style={{ fontSize: 28, fontWeight: 800, marginBottom: 16, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                                    {t('aboutBranch')}
                                </h2>
                                <div style={{ width: 60, height: 4, background: 'var(--sindhi-maroon)', borderRadius: 2, margin: isRTL ? '0 0 0 auto' : '0 auto 0 0' }}></div>
                            </div>

                            <p style={{ color: 'var(--sindhi-text)', lineHeight: 2, fontSize: 17, marginBottom: 40, opacity: 0.9, textAlign: isRTL ? 'right' : 'left', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                                {isRTL ? (branch.descriptionSindhi || branch.description) : (branch.description || t('branchDescriptionDefault'))}
                            </p>

                            {/* Quick Stats */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 24, paddingTop: 40, borderTop: '1px solid var(--sindhi-ivory)' }}>
                                <div style={{ background: 'linear-gradient(135deg, var(--sindhi-ivory), #fff)', padding: 32, borderRadius: 24, border: '1px solid var(--sindhi-gold-muted)' }}>
                                    <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--sindhi-text-muted)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>{t('totalMembers')}</div>
                                    <div style={{ fontSize: 42, fontWeight: 900, color: 'var(--sindhi-navy)' }}>{members.length}</div>
                                </div>
                                <div style={{ background: 'linear-gradient(135deg, var(--sindhi-ivory), #fff)', padding: 32, borderRadius: 24, border: '1px solid var(--sindhi-gold-muted)' }}>
                                    <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--sindhi-text-muted)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>{t('establishedYear')}</div>
                                    <div style={{ fontSize: 42, fontWeight: 900, color: 'var(--sindhi-maroon)' }}>
                                        {branch.established ? new Date(branch.established).getFullYear() : '1992'}
                                    </div>
                                </div>
                            </div>
                        </motion.div>

                        {/* Contact & Info */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
                            <motion.div
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.3 }}
                                className="card-royal"
                                style={{ padding: 32, background: 'white' }}
                            >
                                <h3 className="text-gradient-gold" style={{ fontSize: 13, fontWeight: 900, textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 24, textAlign: isRTL ? 'right' : 'left', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                                    {t('branchSecretary')}
                                </h3>
                                {branch.secretary ? (
                                    <>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 24, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                                            <div style={{
                                                width: 60, height: 60, borderRadius: 18,
                                                background: 'linear-gradient(135deg, var(--sindhi-ivory), #fff)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                fontSize: 24, border: '1px solid var(--sindhi-gold-muted)',
                                                boxShadow: '0 8px 16px -4px rgba(0,0,0,0.05)'
                                            }}>👤</div>
                                            <div style={{ textAlign: isRTL ? 'right' : 'left' }}>
                                                <p style={{ fontSize: 18, fontWeight: 800, color: 'var(--sindhi-navy)', marginBottom: 4, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                                                    {isRTL ? 
                                                        (branch.secretary.full_name_sindhi || branch.secretary.displayNameSindhi || branch.secretary.nameSindhi || branch.secretary.full_name || branch.secretary.displayName || branch.secretary.name) : 
                                                        (branch.secretary.full_name || branch.secretary.displayName || branch.secretary.name)
                                                    }
                                                </p>
                                                <span style={{ fontSize: 12, background: 'var(--sindhi-ivory)', color: 'var(--sindhi-maroon)', padding: '4px 10px', borderRadius: 6, fontWeight: 700, textTransform: 'uppercase', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('secretary')}</span>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, textAlign: isRTL ? 'right' : 'left' }}>
                                            <div style={{ fontSize: 15, color: 'var(--sindhi-text)', display: 'flex', alignItems: 'center', gap: 12, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                                                <span style={{ fontSize: 18 }}>✉️</span> {branch.secretary.contact_info?.email || branch.secretary.email}
                                            </div>
                                            {(branch.secretary.contact_info?.phone || branch.secretary.phone) && (
                                                <div style={{ fontSize: 15, color: 'var(--sindhi-text)', display: 'flex', alignItems: 'center', gap: 12, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                                                    <span style={{ fontSize: 18 }}>📞</span> {branch.secretary.contact_info?.phone || branch.secretary.phone}
                                                </div>
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    <div style={{ padding: '20px', background: 'var(--sindhi-ivory)', borderRadius: 16, fontSize: 14, color: 'var(--sindhi-text-muted)', textAlign: 'center', border: '1px dashed var(--sindhi-gold-muted)' }}>
                                        {t('tbd')}
                                    </div>
                                )}
                            </motion.div>

                            {branch.email && (
                                <motion.div
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.4 }}
                                    className="card-royal"
                                    style={{ padding: 32, background: 'white' }}
                                >
                                    <h3 className="text-gradient-gold" style={{ fontSize: 13, fontWeight: 900, textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 12 }}>
                                        {t('email_label')}
                                    </h3>
                                    <a href={`mailto:${branch.email}`} style={{ color: 'var(--sindhi-maroon)', fontWeight: 800, textDecoration: 'none', fontSize: 18, display: 'block', transition: 'color 0.2s' }}
                                        onMouseEnter={e => e.target.style.color = 'var(--sindhi-navy)'}
                                        onMouseLeave={e => e.target.style.color = 'var(--sindhi-maroon)'}>
                                        {branch.email}
                                    </a>
                                </motion.div>
                            )}
                        </div>
                    </div>

                    {/* Members List */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="card-royal"
                        style={{ marginBottom: 56, overflow: 'hidden', background: 'white' }}
                    >
                        <div style={{ padding: '48px 48px 32px', textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
                            <h2 className="text-gradient-gold" style={{ fontSize: 32, fontWeight: 800, marginBottom: 12, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('approvedMembersCount')} ({members.length})</h2>
                            <p style={{ fontSize: 16, color: 'var(--sindhi-text-muted)', maxWidth: 600, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                                {isRTL ? 'هن شاخ جي تمام فعال ۽ منظور ٿيل ميمبرن جي فهرست جيڪي اسان جي ادبي مشن ۾ حصو وٺن ٿا.' : 'Listing all active and approved members of this branch who contribute to our literary mission.'}
                            </p>
                        </div>
                        {members.length === 0 ? (
                            <div style={{ padding: '80px 48px', textAlign: 'center', color: 'var(--sindhi-text-muted)' }}>
                                <div style={{ fontSize: 64, marginBottom: 24, opacity: 0.3 }}>🍃</div>
                                <p style={{ fontSize: 18, fontWeight: 500 }}>{t('noApprovedMembers')}</p>
                            </div>
                        ) : (
                            <div style={{ overflowX: 'auto', padding: '0 48px 48px' }}>
                                <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 8px', direction: isRTL ? 'rtl' : 'ltr' }}>
                                    <thead>
                                        <tr>
                                            {['name', 'city', 'email'].map(h => (
                                                <th key={h} style={{ padding: '16px 24px', textAlign: isRTL ? 'right' : 'left', fontSize: 12, textTransform: 'uppercase', color: 'var(--sindhi-navy)', fontWeight: 900, letterSpacing: 1.5, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t(h)}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {members.map(m => (
                                            <tr key={m._id} style={{ background: 'var(--sindhi-ivory)', transition: 'transform 0.2s' }}>
                                                <td style={{ padding: '24px', fontWeight: 800, color: 'var(--sindhi-navy)', borderRadius: isRTL ? '0 16px 16px 0' : '16px 0 0 16px', fontSize: 16, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                                                    {isRTL ? (m.full_name_sindhi || m.full_name) : m.full_name}
                                                </td>
                                                <td style={{ padding: '24px', color: 'var(--sindhi-text)', fontWeight: 500, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                                                    {isRTL ? (m.citySindhi || m.city || '—') : (m.city || '—')}
                                                </td>
                                                <td style={{ padding: '24px', color: 'var(--sindhi-text)', fontSize: 15, borderRadius: isRTL ? '16px 0 0 16px' : '0 16px 16px 0' }}>{m.contact_info?.email || m.email}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </motion.div>

                    {/* Activity Feed */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="card-royal"
                        style={{ background: 'white' }}
                    >
                        <div style={{ padding: '48px 48px 24px' }}>
                            <h2 className="text-gradient-gold" style={{ fontSize: 32, fontWeight: 800, marginBottom: 12 }}>{t('recentActivity')}</h2>
                            <p style={{ fontSize: 16, color: 'var(--sindhi-text-muted)' }}>Latest updates, events, and milestones within this local chapter.</p>
                        </div>
                        {activity.length === 0 ? (
                            <div style={{ padding: '80px 48px', textAlign: 'center', color: 'var(--sindhi-text-muted)' }}>
                                <div style={{ fontSize: 64, marginBottom: 24, opacity: 0.3 }}>⏳</div>
                                <p style={{ fontSize: 18, fontWeight: 500 }}>{t('noActivity')}</p>
                            </div>
                        ) : (
                            <div style={{ padding: '0 48px 48px' }}>
                                {activity.map((log, i) => (
                                    <motion.div
                                        key={log._id}
                                        initial={{ opacity: 0, x: -10 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        transition={{ delay: i * 0.05 }}
                                        style={{
                                            padding: '32px 0',
                                            borderBottom: i < activity.length - 1 ? '1px solid var(--sindhi-ivory)' : 'none',
                                            display: 'flex', gap: 24
                                        }}
                                    >
                                        <div style={{
                                            width: 64, height: 64, borderRadius: 20,
                                            background: 'linear-gradient(135deg, var(--sindhi-ivory), #fff)',
                                            color: 'var(--sindhi-maroon)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            flexShrink: 0, fontSize: 28,
                                            border: '1px solid var(--sindhi-gold-muted)',
                                            boxShadow: '0 8px 16px -4px rgba(0,0,0,0.05)'
                                        }}>
                                            {log.actionType === 'member_joined' && '👤'}
                                            {log.actionType === 'status_changed' && '✨'}
                                            {log.actionType === 'officer_added' && '👔'}
                                            {!['member_joined', 'status_changed', 'officer_added'].includes(log.actionType) && '📝'}
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                                <div style={{ fontWeight: 800, color: 'var(--sindhi-navy)', fontSize: 18 }}>{log.action}</div>
                                                <div style={{ fontSize: 13, color: 'var(--sindhi-maroon)', background: 'var(--sindhi-ivory)', padding: '6px 14px', borderRadius: 100, fontWeight: 700 }}>
                                                    {new Date(log.timestamp).toLocaleDateString('en-PK', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                </div>
                                            </div>
                                            <div style={{ fontSize: 14, color: 'var(--sindhi-text-muted)', display: 'flex', alignItems: 'center', gap: 8 }}>
                                                {t('initiatedBy')} <span style={{ fontWeight: 800, color: 'var(--sindhi-navy)' }}>{log.user?.name || t('administrator')}</span>
                                            </div>
                                            {log.details && (
                                                <div style={{
                                                    fontSize: 15, marginTop: 16, color: 'var(--sindhi-text)',
                                                    background: 'var(--sindhi-ivory)', padding: '16px 24px',
                                                    borderRadius: 16, border: '1px solid var(--sindhi-gold-muted)',
                                                    lineHeight: 1.6
                                                }}>
                                                    {log.details}
                                                </div>
                                            )}
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        )}
                    </motion.div>
                </div>

                <div style={{ padding: 40 }} />
            </div>
        </div>
    );
}